import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { MessageCircle, Mail, Phone, MapPin, Clock } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// URL del QR de WhatsApp limpio
const WHATSAPP_QR = "https://customer-assets.emergentagent.com/job_device-repair-pro-18/artifacts/qc5fn55c_IMG-20260610-WA0001.jpg";

export const Contact = () => {
  const [businessHours, setBusinessHours] = useState([]);

  useEffect(() => {
    fetchBusinessHours();
  }, []);

  const fetchBusinessHours = async () => {
    try {
      const response = await fetch(`${API}/business-hours/`);
      if (response.ok) {
        const data = await response.json();
        setBusinessHours(data.hours || []);
      }
    } catch (error) {
      console.error('Error loading hours:', error);
    }
  };

  const handleWhatsAppClick = () => {
    window.open('https://wa.me/543586019885', '_blank');
  };

  return (
    <div className="min-h-screen py-20 px-4">
      <div className="container mx-auto">
        {/* Header */}
        <div className="max-w-3xl mx-auto text-center mb-16">
          <h1 className="text-5xl md:text-6xl font-bold mb-6 bg-gradient-to-r from-cyan-400 to-emerald-400 bg-clip-text text-transparent">
            Contacto
          </h1>
          <p className="text-lg text-slate-400">
            Estamos aquí para ayudarte. Contáctanos por WhatsApp.
          </p>
        </div>

        <div className="grid md:grid-cols-2 gap-8 max-w-6xl mx-auto">
          {/* WhatsApp QR Section */}
          <Card className="bg-slate-800/50 backdrop-blur-sm border-slate-700">
            <CardHeader>
              <CardTitle className="text-white text-2xl flex items-center gap-3">
                <MessageCircle className="h-8 w-8 text-emerald-400" />
                Contáctanos por WhatsApp
              </CardTitle>
              <CardDescription className="text-slate-400 text-base">
                Escanea el código QR para chatear directamente con nosotros
              </CardDescription>
            </CardHeader>
            <CardContent className="flex flex-col items-center p-8">
              {/* QR Code limpio */}
              <div className="bg-white p-6 rounded-2xl shadow-2xl mb-6">
                <img 
                  src={WHATSAPP_QR} 
                  alt="WhatsApp QR KrolBolt" 
                  className="w-80 h-80 object-contain"
                />
              </div>
              
              {/* Instructions */}
              <div className="text-center space-y-4">
                <h3 className="text-white font-semibold text-lg">¿Cómo usar el QR?</h3>
                <ol className="text-slate-300 text-left space-y-2 max-w-md">
                  <li className="flex gap-2">
                    <span className="text-cyan-400 font-bold">1.</span>
                    <span>Abre WhatsApp en tu teléfono</span>
                  </li>
                  <li className="flex gap-2">
                    <span className="text-cyan-400 font-bold">2.</span>
                    <span>Toca Menú (⋮) o Configuración</span>
                  </li>
                  <li className="flex gap-2">
                    <span className="text-cyan-400 font-bold">3.</span>
                    <span>Selecciona "Dispositivos vinculados"</span>
                  </li>
                  <li className="flex gap-2">
                    <span className="text-cyan-400 font-bold">4.</span>
                    <span>Toca "Vincular un dispositivo"</span>
                  </li>
                  <li className="flex gap-2">
                    <span className="text-cyan-400 font-bold">5.</span>
                    <span>Escanea este código QR</span>
                  </li>
                </ol>
                
                <div className="mt-6 pt-6 border-t border-slate-700">
                  <button
                    onClick={handleWhatsAppClick}
                    className="w-full bg-emerald-600 hover:bg-emerald-700 text-white font-semibold py-4 px-6 rounded-lg flex items-center justify-center gap-2 transition-colors"
                  >
                    <MessageCircle className="h-5 w-5" />
                    O haz clic aquí para abrir WhatsApp
                  </button>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Contact Info */}
          <div className="space-y-6">
            {/* Info Cards */}
            <Card className="bg-slate-800/50 backdrop-blur-sm border-slate-700">
              <CardHeader>
                <CardTitle className="text-white">Información de Contacto</CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="flex items-start gap-4">
                  <div className="bg-emerald-600/20 p-3 rounded-lg">
                    <MessageCircle className="h-6 w-6 text-emerald-400" />
                  </div>
                  <div>
                    <h3 className="text-white font-semibold mb-1">WhatsApp</h3>
                    <p className="text-slate-400">+54 358 601-9885</p>
                    <button 
                      onClick={handleWhatsAppClick}
                      className="text-emerald-400 hover:text-emerald-300 text-sm mt-1"
                    >
                      Enviar mensaje →
                    </button>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="bg-cyan-600/20 p-3 rounded-lg">
                    <Mail className="h-6 w-6 text-cyan-400" />
                  </div>
                  <div>
                    <h3 className="text-white font-semibold mb-1">Email</h3>
                    <p className="text-slate-400">info@krolbolt.com.ar</p>
                    <p className="text-slate-400">ventas@krolbolt.com.ar</p>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="bg-purple-600/20 p-3 rounded-lg">
                    <Phone className="h-6 w-6 text-purple-400" />
                  </div>
                  <div>
                    <h3 className="text-white font-semibold mb-1">Teléfono</h3>
                    <p className="text-slate-400">+54 358 601-9885</p>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="bg-orange-600/20 p-3 rounded-lg">
                    <MapPin className="h-6 w-6 text-orange-400" />
                  </div>
                  <div>
                    <h3 className="text-white font-semibold mb-1">Ubicación</h3>
                    <p className="text-slate-400">Río Cuarto, Córdoba</p>
                    <p className="text-slate-400">Argentina</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Business Hours */}
            <Card className="bg-slate-800/50 backdrop-blur-sm border-slate-700">
              <CardHeader>
                <CardTitle className="text-white flex items-center gap-2">
                  <Clock className="h-6 w-6 text-cyan-400" />
                  Horarios de Atención
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {businessHours.map((day, index) => (
                    <div key={index} className="flex justify-between items-center py-2 border-b border-slate-700 last:border-0">
                      <span className="text-slate-300 font-medium">{day.day}</span>
                      {day.is_open ? (
                        <div className="text-right">
                          {day.morning_open && day.morning_close && (
                            <div className="text-emerald-400">
                              <span className="text-slate-500 text-xs">Mañana: </span>
                              {day.morning_open} - {day.morning_close}
                            </div>
                          )}
                          {day.afternoon_open && day.afternoon_close && (
                            <div className="text-emerald-400">
                              <span className="text-slate-500 text-xs">Tarde: </span>
                              {day.afternoon_open} - {day.afternoon_close}
                            </div>
                          )}
                        </div>
                      ) : (
                        <span className="text-red-400">Cerrado</span>
                      )}
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Contact;
